import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import './AdminPages.css';
import './MediaManager.css';

const MediaManager = () => {
  const navigate = useNavigate();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [copiedUrl, setCopiedUrl] = useState('');

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const response = await api.get('/admin/media');
      setMedia(response.data || []);
    } catch (err) {
      setError('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Only image files (JPEG, PNG, GIF, WebP) are allowed');
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError('');
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError('');

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await api.post('/admin/media/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccess('File uploaded successfully!');
      setSelectedFile(null);
      setPreviewUrl('');
      fetchMedia();
      setTimeout(() => setSuccess(''), 3000);

      // Reset file input
      document.getElementById('file-input').value = '';
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await api.delete(`/admin/media/${id}`);
      setSuccess('File deleted successfully!');
      fetchMedia();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete file');
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(''), 2000);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="admin-page">
      <div className="container">
        <div className="page-header">
          <h1>Media Library</h1>
          <button onClick={() => navigate('/admin')} className="btn btn-secondary">
            Back to Dashboard
          </button>
        </div>

        {success && <div className="success">{success}</div>}
        {error && <div className="error">{error}</div>}

        {/* Upload Section */}
        <div className="card">
          <h2>Upload New Image</h2>
          <div className="upload-section">
            <div className="file-input-wrapper">
              <input
                id="file-input"
                type="file"
                onChange={handleFileSelect}
                accept="image/*"
                disabled={uploading}
              />
              <label htmlFor="file-input" className="file-input-label">
                {selectedFile ? selectedFile.name : 'Choose Image'}
              </label>
            </div>

            {previewUrl && (
              <div className="preview-section">
                <img src={previewUrl} alt="Preview" className="upload-preview" />
                <p style={{ marginTop: '10px', color: 'var(--secondary-color)' }}>
                  {selectedFile && formatFileSize(selectedFile.size)}
                </p>
              </div>
            )}

            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="btn btn-primary"
              style={{ marginTop: '15px' }}
            >
              {uploading ? 'Uploading...' : 'Upload Image'}
            </button>
          </div>
        </div>

        {/* Media Gallery */}
        <div className="card" style={{ marginTop: '30px' }}>
          <h2>Uploaded Images ({media.length})</h2>

          {media.length === 0 ? (
            <p style={{ textAlign: 'center', color: 'var(--secondary-color)', padding: '40px' }}>
              No images uploaded yet. Upload your first image above!
            </p>
          ) : (
            <div className="media-grid">
              {media.map((item) => (
                <div key={item.id} className="media-item">
                  <div className="media-image-wrapper">
                    <img
                      src={item.file_path}
                      alt={item.original_filename}
                      className="media-image"
                    />
                  </div>
                  <div className="media-info">
                    <p className="media-filename" title={item.original_filename}>
                      {item.original_filename}
                    </p>
                    <p className="media-meta">
                      {formatFileSize(item.file_size)} • {new Date(item.created_at).toLocaleDateString()}
                    </p>
                    <div className="media-url">
                      <input
                        type="text"
                        value={item.file_path}
                        readOnly
                        onClick={(e) => e.target.select()}
                      />
                      <button
                        onClick={() => copyToClipboard(item.file_path)}
                        className="btn btn-secondary"
                        style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                      >
                        {copiedUrl === item.file_path ? 'Copied!' : 'Copy URL'}
                      </button>
                    </div>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="btn btn-danger"
                      style={{ width: '100%', marginTop: '10px' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaManager;
